import { StrictMode, type ReactNode } from "react"
import { createRoot } from "react-dom/client"
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom"
import { FollowCompany } from "./components/followCompany"
import { Layout } from "./components/layout"
import "./index.css"
import { AuthProvider, DenyExternal, RequireAdmin, RequireAuth, RequirePlatformAdmin } from "./lib/auth"
import { inDesktop } from "./lib/desktop"
import { ToastProvider } from "./components/ui"
import {
  AcceptInvitePage, ForgotPasswordPage, LoginPage, ResetPasswordPage, SignupPage,
} from "./pages/auth"
import {
  AnalyticsPage,
  CreditsPage,
  EmployeesPage,
  SettingsPage,
} from "./pages/admin"
import {
  PlatformAnalyticsPage,
  PlatformAuditPage,
  PlatformCompaniesPage,
  PlatformCompanyDetailPage,
  PlatformModelsPage,
  PlatformPeoplePage,
} from "./pages/platform"
import { ConnectionsPage } from "./pages/connections"
import { DocsPage } from "./pages/docs"
import { DevicePage } from "./pages/device"
import { ContextHubPage, SpacePage } from "./pages/hub"
import { WikiPageView } from "./pages/wiki-page"
import { SessionsPage } from "./pages/sessions"
import { WorkspacesPage } from "./pages/workspaces"
import { WorkspaceSessionsPage } from "./pages/workspaceSessions"
import { WorkspaceServersPage } from "./pages/workspaceServers"
import { PlatformSettingsPage } from "./pages/platformSettings"
import { PlatformMigrationsPage } from "./pages/platformMigrations"
import { SessionTerminalPage } from "./pages/sessionTerminal"
import { DashboardPage, ProjectsPage } from "./pages/work"
import { ProjectSettingsPage } from "./pages/projectSettings"
import { ProjectDetailPage } from "./pages/board"
import { IssuesPage } from "./pages/issues"
import { TaskDetailPage } from "./pages/task"
import { InboxPage } from "./pages/inbox"
import { CalendarPage } from "./pages/calendar"
import { MigrationRunPage, MigrationsPage, NewMigrationPage } from "./pages/migrations"
import { TimeReportPage } from "./pages/timeReport"
import { TimesheetsPage } from "./pages/timesheets"

/** The per-project analytics page is gone; its hours report became the
 *  company-wide Time Report, pre-filtered to the project the URL named. */
function AnalyticsRedirect() {
  const { projectId = "" } = useParams()
  return <Navigate to={`/time-report?projectIds=${projectId}`} replace />
}

// Cloud workspace sessions have no place inside the desktop app, where
// sessions run on the user's own machine. The nav hides these pages there
// (layout.tsx); this is what stops a typed URL — the same convention as the
// admin routes below.
function CloudOnly({ children }: { children: ReactNode }) {
  return inDesktop ? <Navigate to="/" replace /> : <>{children}</>
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
        <Routes>
          {/* Unauthenticated */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/accept-invite" element={<AcceptInvitePage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Public on purpose: the reader may have no account yet. */}
          <Route path="/docs" element={<DocsPage />} />
          <Route path="/downloads" element={<Navigate to="/docs#downloads" replace />} />

          {/* Everything else requires a session */}
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/inbox" element={<InboxPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/projects/:projectId" element={<FollowCompany kind="project" param="projectId"><ProjectDetailPage /></FollowCompany>} />
            <Route path="/projects/:projectId/analytics" element={<AnalyticsRedirect />} />
            {/* Admin-gated by the API; the page shows the 403 to anyone else. */}
            <Route path="/projects/:projectId/settings" element={<FollowCompany kind="project" param="projectId"><ProjectSettingsPage /></FollowCompany>} />
            <Route path="/projects/:projectId/issues" element={<FollowCompany kind="project" param="projectId"><IssuesPage /></FollowCompany>} />
            <Route path="/time-report" element={<TimeReportPage />} />
            <Route path="/timesheets" element={<TimesheetsPage />} />
            <Route path="/tasks/:taskId" element={<FollowCompany kind="task" param="taskId"><TaskDetailPage /></FollowCompany>} />
            <Route path="/workspaces" element={<CloudOnly><DenyExternal><WorkspacesPage /></DenyExternal></CloudOnly>} />
            <Route path="/sessions" element={<CloudOnly><DenyExternal><SessionsPage /></DenyExternal></CloudOnly>} />
            <Route path="/workspaces/:workspaceId/sessions" element={<CloudOnly><DenyExternal><WorkspaceSessionsPage /></DenyExternal></CloudOnly>} />
            {/* Inside the shell like every other page so the nav sidebar stays
                with the session; Layout renders this path full-bleed — no
                padding, no max width, no outer scroll — because the chat,
                terminal and file views own their own height. */}
            <Route path="/workspace-sessions/:sessionId" element={<CloudOnly><DenyExternal><SessionTerminalPage /></DenyExternal></CloudOnly>} />
            {/* Admin-only behind the API too — where a company's source gets
                cloned to is not a per-developer choice. */}
            <Route path="/workspace-servers" element={<CloudOnly><RequireAdmin><WorkspaceServersPage /></RequireAdmin></CloudOnly>} />
            <Route path="/context-hub" element={<ContextHubPage />} />
            <Route path="/context-hub/spaces/:spaceId" element={<FollowCompany kind="space" param="spaceId"><SpacePage /></FollowCompany>} />
            <Route path="/context-hub/pages/:pageId" element={<FollowCompany kind="page" param="pageId"><WikiPageView /></FollowCompany>} />
            <Route path="/analytics" element={<DenyExternal><AnalyticsPage /></DenyExternal>} />
            <Route path="/settings" element={<SettingsPage />} />
            {/* Reached from the terminal. Behind RequireAuth like everything
                else, so an unauthenticated visitor lands on login and is
                returned here afterwards. */}
            <Route path="/device" element={<DevicePage />} />

            {/* Admin-only. The nav hides these from developers; the guard is
                what stops a typed URL. */}
            <Route path="/employees" element={<RequireAdmin><EmployeesPage /></RequireAdmin>} />
            <Route path="/credits" element={<RequireAdmin><CreditsPage /></RequireAdmin>} />
            {/* Open to every employee since ROA-8: personal Google Drive
                connections live here, and SAP/Dynatrace rows were always
                company-scoped rather than secret per person. */}
            <Route path="/connections" element={<DenyExternal><ConnectionsPage /></DenyExternal>} />
            <Route path="/migrations" element={<RequireAdmin><MigrationsPage /></RequireAdmin>} />
            <Route path="/migrations/new" element={<RequireAdmin><NewMigrationPage /></RequireAdmin>} />
            <Route path="/migrations/:id" element={<RequireAdmin><MigrationRunPage /></RequireAdmin>} />
            {/* Moderniser staff only. The guard stops a typed URL; the API
                behind these answers 404 to non-staff regardless. */}
            <Route
              path="/platform/companies"
              element={<RequirePlatformAdmin><PlatformCompaniesPage /></RequirePlatformAdmin>}
            />
            <Route
              path="/platform/companies/:companyId"
              element={<RequirePlatformAdmin><PlatformCompanyDetailPage /></RequirePlatformAdmin>}
            />
            <Route
              path="/platform/people"
              element={<RequirePlatformAdmin><PlatformPeoplePage /></RequirePlatformAdmin>}
            />
            <Route
              path="/platform/models"
              element={<RequirePlatformAdmin><PlatformModelsPage /></RequirePlatformAdmin>}
            />
            <Route
              path="/platform/analytics"
              element={<RequirePlatformAdmin><PlatformAnalyticsPage /></RequirePlatformAdmin>}
            />
            <Route
              path="/platform/audit"
              element={<RequirePlatformAdmin><PlatformAuditPage /></RequirePlatformAdmin>}
            />
            <Route
              path="/platform/migrations"
              element={<RequirePlatformAdmin><PlatformMigrationsPage /></RequirePlatformAdmin>}
            />
            <Route
              path="/platform/settings"
              element={<RequirePlatformAdmin><PlatformSettingsPage /></RequirePlatformAdmin>}
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  </StrictMode>,
)
